// src/review/canvas/shared/EditableSelectField.jsx
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Stack, Text, Select, Group, Button, Badge, Tooltip } from '@mantine/core';
import { IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { trackEdit } from '../../../redux/slices/reviewSlice';
import { useReview } from '../../context/ReviewContext';

/**
 * EditableSelectField - Reusable inline editable select/dropdown component
 * Similar to EditableField but for select inputs (priority, status, etc.)
 */
function EditableSelectField({
  label,
  value,
  itemType,
  itemId,
  fieldName,
  options,
  placeholder,
  readOnly,
  onSave,
  getColor,
}) {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useReview();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);

  // Get user info from app state
  const userInfo = useSelector((state) => {
    const user = state.app?.currentUser;
    return user ? {
      userId: user.sub || user.id || user.email,
      email: user.email,
      name: user.name || user.email?.split('@')[0],
    } : null;
  });

  // Get edit history for this field from RTK review state
  const reviewState = useSelector((state) =>
    state.review.byAssessment[assessmentUuid] || {}
  );
  const edits = reviewState.edits || {};

  // Find if this field has been edited
  const fieldEdit = Object.values(edits).flatMap(category => Object.values(category))
    .find(edit =>
      edit.itemId === itemId &&
      edit.fieldName === fieldName
    );

  const handleEdit = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(true);
  }, [value]);

  const handleCancel = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(false);
  }, [value]);

  const handleSave = useCallback(async () => {
    // Skip if no change
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      // 1. Call custom save handler if provided (for DB persistence)
      if (onSave) {
        await onSave(editValue, value);
      }

      // 2. Track edit for audit trail
      await dispatch(trackEdit({
        modelId,
        assessmentUuid,
        itemType,
        itemId,
        field: fieldName,
        oldValue: value,
        newValue: editValue,
        userInfo
      }));
      setIsEditing(false);
    } catch (error) {
      console.error('[EditableSelectField] Error saving edit:', error);
    } finally {
      setSaving(false);
    }
  }, [editValue, value, onSave, dispatch, modelId, assessmentUuid, itemType, itemId, fieldName, userInfo]);

  if (readOnly) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={600} c="dimmed">{label}</Text>
        <Badge
          size="lg"
          variant="filled"
          color={getColor ? getColor(value) : 'gray'}
        >
          {value || placeholder || 'N/A'}
        </Badge>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600} c="dimmed">{label}</Text>
        {fieldEdit && !isEditing && (
          <Tooltip label={`Edited by ${fieldEdit.editorName || fieldEdit.editorEmail || 'Unknown'}`}>
            <Badge size="xs" variant="light" color="blue">
              Edited
            </Badge>
          </Tooltip>
        )}
      </Group>

      {isEditing ? (
        <Stack gap="xs">
          <Select
            value={editValue}
            onChange={setEditValue}
            data={options}
            placeholder={placeholder}
            searchable
            autoFocus
          />
          <Group gap="xs">
            <Button
              size="xs"
              leftSection={<IconCheck size={14} />}
              onClick={handleSave}
              loading={saving}
            >
              Save
            </Button>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconX size={14} />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <Group
          gap="xs"
          style={{
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #e9ecef',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'inline-flex',
            maxWidth: 'fit-content',
          }}
          onClick={handleEdit}
        >
          <Badge
            size="lg"
            variant="filled"
            color={getColor ? getColor(value) : 'gray'}
          >
            {value || placeholder || 'Click to edit...'}
          </Badge>
          <IconEdit size={16} color="#868e96" />
        </Group>
      )}
    </Stack>
  );
}

EditableSelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  itemType: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ])).isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
  getColor: PropTypes.func,
};

EditableSelectField.defaultProps = {
  value: '',
  placeholder: '',
  readOnly: false,
  onSave: null,
  getColor: null,
};

export default EditableSelectField;
